<template>
  <div class="reply-page__wrapper">
    <div class="reply-page__logo">
      <img alt="" src="@/assets/images/logo_2.png" />
    </div>
    <SPreloaderDebounced v-if="loading" :is-active="loading" :delay="1000" :deactivation-delay="2000" />
    <div v-else-if="info && info.length && !loading" class="reply-page">
      <div class="reply-page__title">Моё портфолио</div>

      <section class="reply-page__card reply-page__card_type_profile profile">
        <div class="profile__avatar">
          <img 
            :src="avatarSrc" 
            alt="Аватар" 
            class="profile__avatar-image" 
          />
        </div>

        <div class="profile__info">
          <p class="profile__name">
            {{ FIO }}
          </p>
        </div>
      </section>

      <div v-for="(value, key) in info" :key="key" class="reply-page__item">
        <section class="reply-page__card reply-page__card_type_project project">
          <div class="project__header">
            <h2 class="project__title">
              {{ value?.title }}
            </h2>
          </div>

          <div class="project__content">
            <div class="project__description">
              {{ value.description }}
            </div>

            <div 
              v-if="value?.materials_link" 
              class="project__field project__field_type_link"
            >
              <span class="project__label">Ссылка на материалы</span>
              <a 
                :href="value.materials_link" 
                target="_blank" 
                class="project__link"
              >
                {{ value.materials_link }}
              </a>
            </div>

            <div 
              v-if="value.attached_file" 
              class="project__field project__field_type_file"
            >
              <span class="project__label">Прикреплённый файл</span>
              <SUploaderMini 
                v-if="value.attached_file" 
                :removable="false" 
                :files="[value.attached_file]" 
                class="project__uploader"
                :downloadable="false"
              />
            </div>
          </div>
        </section>
      </div>
    </div>
    <div v-else class="reply-page__empty">
      <img alt="" src="@/assets/images/empty.png" />
      <div>
        <b>Портфолио недоступно</b>
      </div>
      <div style="font-size: 16px;">Все записи в портфолио были удалены.</div>
      <div style="font-size: 16px;">Для получения актуальной информации запросите новую ссылку</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { SPreloaderDebounced, SUploaderMini, useToast } from '@synergy/ui-kit-vue3'
import axios from 'axios'
import ENV from '@/env.json'
import type { Fields, Personal } from '@/types/Portfolio/portfolio.types'
import defaultAvatar from '@/assets/images/avatar.png';


const route = useRoute()
const router = useRouter()
const toast = useToast()

const token = route.params.outer_token as string
const loading = ref(false)
const info = ref<Fields[]>([])
const personal = ref<Personal>()

const FIO = computed(() => {
  if (!personal.value) return '';
  
  const parts = [
    personal.value.last_name,
    personal.value.first_name,
    personal.value.middle_name
  ].filter(part => part && part.trim() !== '');
  
  return parts.join(' ');
});

const avatarSrc = computed(() => {
  return personal.value?.avatar_url || defaultAvatar;
});

onMounted(async () => {
  updateData()
})

async function updateData() {
  loading.value = true
  await fetchInfo()
  loading.value = false
}

async function fetchInfo(): Promise<Fields[]|void> {
  try {
    const { data } = await axios.get(`${ENV.PORTFOLIO__API_URL}/portfolio/outer/${token}`)
    info.value = data.data.map((item: Fields) => ({
      ...item,
      attached_file: item.attached_file ? {
        id: item.attached_file.id,
        name: item.attached_file.file_name,
        size: item.attached_file.file_size,
        link: item.attached_file.link,
        type: item.attached_file.mime_type,
        file_name: item.attached_file.file_name,
        file_size: item.attached_file.file_size,
        mime_type: item.attached_file.mime_type
      } : null
    }))
    personal.value = data.profile
  } catch (error: unknown) {
    console.error(error)
    toast.add({
      severity: 'error',
      summary: 'Произошла ошибка при запросе информации',
      life: 3000,
    })

    router.push({ name: 'not_found' })
  }
}
</script>

<style lang="scss" scoped>
:deep(.sui-uploader-mini__file) {
  background-color: #F5F5F5 !important;
}
.reply-page {
  max-width: 1540px;
  background: #fff;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  border-radius: 16px;
  margin: 0 auto;
  height: calc(100% - 24px);
  overflow-y: scroll;
  padding: 24px 20px;

  &__logo {
    height: 32px;
    margin-bottom: 20px;
    & img {
      background-size: contain;
      height: 32px;
    }
  }
  
  &__wrapper {
    max-width: 1540px;
    width: 100%;
    margin: 0 auto;
    padding: 24px;
    height: calc(100% - 24px);
  }
  
  &__title {
    font-weight: 500;
    font-size: 28px;
    line-height: 36px;
    margin-bottom: 16px;
    color: #1C1C1C;
  }

  &__item {
    display: flex;
    flex-direction: column;
    gap: 16px;
    background: #fff;
    border-radius: 16px;
  }

  &__card {
    background: #fff;
    border: 1px solid #e5e5e5;
    border-radius: 12px;
    padding: 24px;
    
    &_type_profile {
      margin-bottom: 0;
    }
  }
  
  &__empty {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    color: #87898F;
    background: #fff;
    height: calc(100% - 24px);
    flex-direction: column;
    gap: 4px;
    font-size: 18px;
    line-height: 24px;
    border-radius: 16px;
  }
}

.profile {
  display: flex;
  gap: 16px;

  &__avatar {
    flex-shrink: 0;
    
    &-image {
      width: 64px;
      height: 64px;
      border-radius: 50%;
      object-fit: cover;
    }
  }

  &__info {
    flex: 1;
    min-width: 0;
    display: flex;
  }

  &__name {
    font-weight: 500;
    font-size: 22px;
    line-height: 30px;
    color: #1C1C1C;
    margin: 0;
    display: flex;
    align-items: center;
  }
}

.project {
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__header {
    margin-bottom: 4px;
  }

  &__title {
    font-weight: 500;
    font-size: 22px;
    line-height: 30px;
    color: #1C1C1C;
    margin: 0;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__description {
    width: 100%;
    font-weight: 400;
    font-size: 16px;
    line-height: 22px;
    color: #1C1C1C;
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 8px;
    
    &_type_link {
      margin-top: 4px;
    }
    
    &_type_file {
      margin-top: 4px;
    }
  }

  &__label {
    font-weight: 500;
    font-size: 18px;
    line-height: 24px;
    color: #1C1C1C;
  }

  &__link {
    color: #2C50EE;
    text-decoration: underline;
    font-weight: 500;
    font-size: 16px;
    line-height: 16px;
    word-break: break-all;
    
    &:hover {
      text-decoration: none;
    }
  }
  
  &__uploader {
    margin-top: 4px;
  }
}

@media (max-width: 600px) {
  .reply-page {
    padding: 16px;
    
    &__title {
      font-size: 24px;
      line-height: 32px;
    }
    
    &__card {
      padding: 16px;
    }
  }
  
  .profile {
    gap: 12px;
    
    &__name {
      font-size: 18px;
      line-height: 24px;
    }
  }
  
  .project {
    &__title {
      font-size: 18px;
      line-height: 24px;
    }
    
    &__description {
      font-size: 14px;
      line-height: 20px;
    }
    
    &__label {
      font-size: 16px;
      line-height: 22px;
    }
    
    &__link {
      font-size: 14px;
    }
  }
}
</style>